'use client';

import { Table } from '@tanstack/react-table';
import { useCallback, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import { readExcel, writeExcel } from 'src/lib/xlsx/utils';

import type { ImportResult, UseDataTableImportReturn } from './use-data-table-import';
import { getTableHeader } from './utils';

export interface UseDataTableImportClientOptions<
  TData = any,
  TSchema extends z.ZodType = z.ZodType,
> {
  /** 表格实例 */
  table: Table<TData>;
  /** 导入数据校验 schema */
  schema: TSchema;
  /** 工作表名称 */
  sheetName?: string;
  /**
   * 手动指定导入列配置（可选）
   * 如果不指定，则从 table 中自动提取（但可能会遗漏隐藏的列）
   */
  importColumns?: Array<{ key: string; title: string; width?: number }>;
  /**
   * 处理单条数据的函数
   * @param data 校验后的数据
   * @returns Promise<void>
   */
  processData: (data: z.infer<TSchema>) => Promise<void>;
  /** 导入成功回调 */
  onSuccess?: () => void;
}

/**
 * 客户端数据表格导入 Hook
 *
 * 使用 ExcelJS 在浏览器中解析 Excel 文件，适用于小到中等数据量（<10000行）
 *
 * @example
 * ```tsx
 * const importer = useDataTableImportClient({
 *   table,
 *   schema: importConnectorSchema,
 *   sheetName: '充电枪信息',
 *   processData: async (data) => {
 *     await updateConnectorByNo(data);
 *   },
 *   onSuccess: () => router.refresh(),
 * });
 *
 * <DataTableImporter title="导入充电枪" {...importer} />
 * ```
 */
export function useDataTableImportClient<TData = any, TSchema extends z.ZodType = z.ZodType>(
  options: UseDataTableImportClientOptions<TData, TSchema>
): UseDataTableImportReturn {
  const { table, schema, sheetName = 'Sheet1', importColumns, processData, onSuccess } = options;

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | undefined>(undefined);
  const cancelRef = useRef(false);

  // 从 TanStack Table 获取表头信息（导入不过滤隐藏列，但过滤 unimportable 列）
  // 或使用手动指定的列配置
  const header = useMemo(() => {
    if (importColumns) {
      // 手动指定的列配置，转换为 XlsxColumn 格式
      return [
        importColumns.map((col) => ({
          title: col.title,
          key: col.key,
          width: col.width || 30,
        })),
      ];
    }
    return getTableHeader(table, { forImport: true });
  }, [table, importColumns]);

  const handleResult = (newResult: ImportResult | undefined) => {
    if (newResult === undefined) setResult(undefined);
    else setResult((oldResult) => ({ ...oldResult, ...newResult }));
  };

  // 判断是否成功
  const isSuccess = useCallback(() => {
    return result?.success !== undefined;
  }, [result]);

  // 判断是否错误
  const isError = useCallback(() => {
    return result?.error !== undefined;
  }, [result]);

  // 判断是否已导入过数据
  const hasImported = useCallback(() => {
    return Boolean(result?.progress || result?.success);
  }, [result]);

  // 重置状态
  const reset = useCallback(() => {
    setLoading(false);
    handleResult(undefined);
    cancelRef.current = false;
  }, []);

  // 处理对话框状态变化
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !loading) {
        // 关闭对话框时，如果有导入过数据，调用 onSuccess
        if (hasImported()) {
          onSuccess?.();
        }

        // 重置所有状态
        reset();
      }
      setOpen(newOpen);
    },
    [loading, hasImported, onSuccess, reset]
  );

  // 取消导入
  const cancelImport = useCallback(() => {
    cancelRef.current = true;
    setLoading(false);
    // 取消时保留当前进度信息
  }, []);

  // 下载模板
  const handleDownloadTemplate = useCallback(
    async (filename: string) => {
      try {
        // 使用 writeExcel 生成模板文件
        const buffer = await writeExcel(header, sheetName);

        // 触发下载
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Failed to download template:', error);
      }
    },
    [header, sheetName]
  );

  // 执行导入
  const handleImport = useCallback(
    async (file: File) => {
      // 重置状态
      setLoading(true);
      handleResult(undefined);
      cancelRef.current = false;

      try {
        // 读取文件
        const arrayBuffer = await file.arrayBuffer();

        // 解析 Excel
        handleResult({ progress: 10, message: '正在读取文件...' });
        const rawData = await readExcel(arrayBuffer, header, sheetName);

        if (cancelRef.current) return;

        // 检查是否有有效数据
        if (rawData.length === 0) {
          setLoading(false);
          handleResult({
            error: {
              message: '文件中没有有效数据！',
              details: [{ message: '请检查文件是否包含数据行，或数据格式是否正确' }],
            },
          });
          return;
        }

        // 数据校验
        handleResult({ progress: 20, message: '正在校验数据...' });
        const validatedData: z.infer<TSchema>[] = [];
        const validationErrors: Array<{ message: string }> = [];

        // 获取表头最后一层（数据列）
        const dataHeader = header[header.length - 1] || [];

        for (let i = 0; i < rawData.length; i++) {
          try {
            const validated = schema.parse(rawData[i]);
            validatedData.push(validated);
          } catch (err) {
            if (err instanceof z.ZodError) {
              // 按字段分组错误信息，参考服务器端处理方式
              const fieldErrors = err.flatten().fieldErrors;

              // 为每个字段的错误生成详细错误信息
              Object.entries(fieldErrors).forEach(([field, messages]) => {
                if (messages && messages.length > 0) {
                  // 查找字段对应的中文标题
                  const fieldTitle = dataHeader.find((col) => col.key === field)?.title || field;
                  const errorMessage = Array.isArray(messages)
                    ? messages.join('|')
                    : '数据格式错误';

                  // Excel 行号 = 数据索引 + 表头行数 + 1
                  const excelRowNumber = i + header.length + 1;

                  validationErrors.push({
                    message: `文件第${excelRowNumber}行 ${fieldTitle}: ${errorMessage}`,
                  });
                }
              });
            } else {
              const excelRowNumber = i + header.length + 1;
              validationErrors.push({
                message: `文件第${excelRowNumber}行: 数据校验失败`,
              });
            }
          }
        }

        if (cancelRef.current) return;

        // 如果有校验错误，直接返回
        if (validationErrors.length > 0) {
          setLoading(false);
          handleResult({
            error: {
              message: `数据校验失败，共 ${validationErrors.length} 条错误`,
              details: validationErrors,
            },
          });
          return;
        }

        // 批量处理数据
        handleResult({ progress: 30, message: '正在导入数据...' });
        const processingErrors: Array<{ message: string }> = [];
        const totalCount = validatedData.length;
        let processedCount = 0;

        const results = await Promise.allSettled(
          validatedData.map(async (data, idx) => {
            if (cancelRef.current) throw new Error('取消导入');

            try {
              await processData(data);
              processedCount++;

              // 更新进度
              const progress = Math.floor(30 + (processedCount / totalCount) * 60);
              handleResult({
                progress,
                message: `正在导入数据... (${processedCount}/${totalCount})`,
              });
            } catch (error) {
              const excelRowNumber = idx + header.length + 1;
              throw {
                index: idx,
                error: error instanceof Error ? error.message : '未知错误',
                rowNumber: excelRowNumber,
              };
            }
          })
        );

        if (cancelRef.current) return;

        // 收集失败的错误
        results.forEach((result) => {
          if (result.status === 'rejected') {
            const reason = result.reason as { index: number; error: string; rowNumber: number };
            processingErrors.push({
              message: `文件第${reason.rowNumber}行: ${reason.error}`,
            });
          }
        });

        setLoading(false);

        // 如果有处理错误
        if (processingErrors.length > 0) {
          const successCount = totalCount - processingErrors.length;
          handleResult({
            progress: 100,
            error: {
              message: `部分导入失败：成功 ${successCount} 条，失败 ${processingErrors.length} 条`,
              details: processingErrors,
            },
          });
        } else {
          // 全部成功
          handleResult({
            progress: 100,
            message: '导入成功',
            success: true,
            data: { count: totalCount },
          });
        }
      } catch (error: any) {
        if (cancelRef.current) return;

        setLoading(false);
        handleResult({
          error: {
            message: error instanceof Error ? error.message : '导入失败',
            details: error.details || [{ message: '导入过程中发生未知错误' }],
          },
        });
      }
    },
    [header, sheetName, schema, processData]
  );

  return {
    open,
    onOpenChange: handleOpenChange,
    loading,
    result,
    handleImport,
    handleDownloadTemplate,
    cancelImport,
    reset,
    isSuccess,
    isError,
    hasImported,
  };
}
